import { useState } from "react";
import { Link } from "react-router-dom";
import type { MachineDashboardRow } from "../../types/machinesDashboard";
import { useAuth } from "../../auth/AuthContext";
import { alarmsApi } from "../../api/alarmsApi";
import "../../styles/irinox.css";

export function MachinesTable({
  provider,
  machines,
}: {
  provider: string;
  machines: MachineDashboardRow[];
}) {
  const { token } = useAuth() as any;
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const isIrinox = provider === "IRINOX";

  const handleAckAll = async (machineId: string, hasAlarms: boolean) => {
    if (!hasAlarms) return;
    if (!token) {
      alert("Sessione scaduta: rifai login per spegnere gli allarmi.");
      return;
    }

    setBusyIds((prev) => {
      const next = new Set(prev);
      next.add(machineId);
      return next;
    });

    try {
      await alarmsApi.ackResetForMachine(token, machineId, {
        note: "ACK/RESET da lista macchine",
        force: false,
      });
    } catch {
      alert("Errore nello spegnere gli allarmi della macchina.");
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(machineId);
        return next;
      });
    }
  };

  return (
    <div className={`machines-section-card shadow-sm ${isIrinox ? "irinox-card" : "card"}`}>
      <div
        className={`d-flex align-items-center justify-content-between ${
          isIrinox ? "irinox-card-header" : "card-header"
        }`}
      >
        <div className="d-flex align-items-center gap-2">
          {isIrinox && <i className="bi bi-list-ul" />}
          <div className="fw-semibold">Lista macchine</div>
        </div>
        <span className={`badge ${isIrinox ? "irinox-badge-info" : "text-bg-light"}`}>{machines.length}</span>
      </div>

      <div className="table-responsive">
        <table
          className={`table mb-0 align-middle ${isIrinox ? "irinox-table" : "table-hover"}`}
        >
          <thead className={isIrinox ? "" : "table-light"}>
            <tr>
              <th>Nome</th>
              <th>Serial</th>
              <th>Stato</th>
              <th>Allarmi</th>
              <th className="text-end">Azioni</th>
            </tr>
          </thead>

          <tbody>
            {machines.map((m) => {
              const alarmsCount = m.alarmsActive ?? 0;
              const busy = busyIds.has(m.id);
              const energyToday = m.telemetryLatest?.energyKwhToday ?? null;
              const temperature = m.telemetryLatest?.temperatureC ?? null;

              return (
                <tr key={m.id}>
                  <td className="fw-semibold">
                    {m.name}
                    {isIrinox && (
                      <div className="small text-muted mt-1">
                        {energyToday != null && (
                          <span className="me-3">
                            <i className="bi bi-lightning-charge me-1" />
                            {energyToday.toFixed(1)} kWh oggi
                          </span>
                        )}
                        {temperature != null && (
                          <span>
                            <i className="bi bi-thermometer-half me-1" />
                            {temperature.toFixed(1)}°C
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="text-muted">{m.externalId ?? "-"}</td>

                  <td>
                    <span
                      className={`badge ${
                        m.status === "ONLINE"
                          ? "text-bg-success"
                          : m.status === "ALARM"
                          ? "text-bg-danger"
                          : "text-bg-secondary"
                      }`}
                    >
                      {m.status ?? "UNKNOWN"}
                    </span>
                  </td>

                  <td>{alarmsCount}</td>

                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={!alarmsCount || busy}
                        onClick={() => handleAckAll(m.id, !!alarmsCount)}
                      >
                        {busy ? "..." : "Spegni allarmi"}
                      </button>

                      {provider === "IRINOX" && (
                        <Link
                          to={`/machines/IRINOX/${m.id}/analytics`}
                          className="btn btn-sm btn-outline-info"
                        >
                          Analytics
                        </Link>
                      )}
                      {provider === "IFI_GALILEO" && (
                        <Link
                          to={`/machines/IFI_GALILEO/${m.id}/analytics`}
                          className="btn btn-sm btn-outline-info"
                        >
                          Analytics
                        </Link>
                      )}
                      {provider === "RATIONAL" && (
                        <Link
                          to="/machines/RATIONAL/analytics"
                          className="btn btn-sm btn-outline-info"
                        >
                          Analytics
                        </Link>
                      )}
                      <Link
                        to={`/machines/${provider}/${m.id}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Dettaglio
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!machines.length && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  Nessuna macchina trovata
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}